import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FlexyForm, FormObject } from '@europrocurement/flexy-form';
import { Box } from '@mui/material';
import { SubmitHandler, useForm } from 'react-hook-form';
import { GenericModalMailType } from '@europrocurement/flexy-components/types/GenericModalMailType';
import { useModal, useUploader } from '@europrocurement/flexy-components';
import { MailApiSendMailMailCollectionRequest } from '@europrocurement/l2d-domain/openApi/ApiMail';
import { useMailService } from '@europrocurement/l2d-domain';
import { useSnackbar } from 'notistack';
import { type ContactItem } from '@europrocurement/l2d-modules/modules/Dossiers';
import { UseKeycloakService } from '@europrocurement/l2d-keycloak';
import { markDownToHtml } from '@europrocurement/l2d-utils';
import useFormStructureGenericModal from '../../hooks/useFormStructureGenericModal';

const GenericModalMailContent: React.FunctionComponent<GenericModalMailType> = function ({
    to,
    cc,
    cci,
    subject,
    body,
    attachments,
    prescriber,
    showTo = false,
    showCC = false,
    showCCI = false,
    showSubject = false,
    showBody = false,
    showAttachments = false,
    SxProps,
    customButtonsFooter,
    fieldsOrdering,
    getFormContext,
    autoBalisesForMail = false,
}) {
    const { files } = useUploader();
    const { enqueueSnackbar } = useSnackbar();
    const { mailApi } = useMailService();
    const { modalActions } = useModal();
    const keycloak = UseKeycloakService();
    const [submittingForm, setSubmittingForm] = useState(false);
    const senderEmail = useRef('');
    const senderName = useRef('');

    const { getFormStructure } = useFormStructureGenericModal({
        to,
        cc,
        cci,
        subject,
        body,
        attachments,
        prescriber,
        showTo,
        showCC,
        showCCI,
        showSubject,
        showBody,
        showAttachments,
        SxProps,
        customButtonsFooter,
        fieldsOrdering,
    });
    type MailFormObject = {
        recipients: ContactItem[] | undefined;
        recipientsCC: ContactItem[] | undefined;
        recipientsCCI: ContactItem[] | undefined;
        subject: string | undefined;
        mailBody: string | undefined;
    };

    useEffect(() => {
        senderEmail.current = keycloak.getEmail();
        senderName.current = keycloak.getName();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleSubmit = useCallback<SubmitHandler<MailFormObject>>(
        async (values) => {
            setSubmittingForm(true);
            let mailRecipientsTo = to.map((recipient) => recipient.email);
            if (values.recipients && values.recipients.length > 0) {
                mailRecipientsTo = values.recipients.map((recipient) => recipient.emailAddress);
            }

            // Is it a good idea to check recipientsCC length > 0 if trying to remove original pre values ??????
            let mailRecipientsCC = cc;
            if (values.recipientsCC && values.recipientsCC.length > 0) {
                mailRecipientsCC = values.recipientsCC.map((recipient) => recipient.emailAddress);
            }

            let mailRecipientsCCI = cci;
            if (values.recipientsCCI && values.recipientsCCI.length > 0) {
                mailRecipientsCCI = values.recipientsCCI.map((recipient) => recipient.emailAddress);
            }

            let mailSubject = subject;
            if (values.subject) {
                mailSubject = values.subject;
            }

            let mailBody = body;
            if (values.mailBody) {
                mailBody = values.mailBody as string;
            }
            if (autoBalisesForMail && mailBody) {
                mailBody = markDownToHtml({ content: mailBody });
            }

            const request: MailApiSendMailMailCollectionRequest = {
                attachments2: files,
                details: {
                    from: senderEmail.current,
                    fromName: senderName.current,
                    tos: mailRecipientsTo,
                    ccs: mailRecipientsCC || null,
                    bccs: mailRecipientsCCI || null,
                    subject: mailSubject || null,
                    body: mailBody || null,
                    status: 'pending',
                    tags: ['?'],
                    replies: [senderEmail.current],
                    // @ts-expect-error : Let's ignore undefined property
                    useTemplating: true,
                    templateData: {
                        contentTemplate: 'default-content',
                        mailContent: mailBody,
                        logo: {
                            alt: 'Legal2Digital Logo',
                            src: '/shared/images/LogoLegal2DigitalLight.png',
                            title: 'Legal2Digital',
                        },
                    },
                },
            };

            const emailPromise = await mailApi.sendMailMailCollection(request);
            setSubmittingForm(false);
            if (emailPromise.status === 201) {
                enqueueSnackbar('Le mail a bien été envoyé');
            } else {
                enqueueSnackbar("Une erreur est survenue lors de l'envoi du mail", {
                    variant: 'error',
                });
            }
            modalActions.close();
        },
        [
            files,
            body,
            cc,
            cci,
            subject,
            to,
            mailApi,
            enqueueSnackbar,
            modalActions,
            autoBalisesForMail,
        ],
    );

    const formContext = useForm({
        mode: 'onTouched',
        defaultValues: {
            mailBody: body || '',
            subject: subject || '',
        } as FormObject,
    });

    const formProps = {
        formContext,
        formObject: {},
        onSubmit: handleSubmit,
        gridProps: { sx: { gap: '8px' } },
        submitButton: {
            label: 'Envoyer',
            displayed: false,
        },
        isSubmitting: submittingForm,
    };

    // Method to get the current form context from parent
    if (typeof getFormContext !== 'undefined' && typeof getFormContext === 'function') {
        getFormContext(formContext);
    }

    return (
        <Box sx={{ minWidth: '20rem', maxWidth: '40rem', ...SxProps }}>
            <FlexyForm
                formStructure={getFormStructure()}
                {...formProps}
            />
        </Box>
    );
};

export default GenericModalMailContent;
